<template>
  <div class="val-wrap" ref="wrap">
    <!-- Section header -->
    <div class="val-header">
      <p class="val-eyebrow">{{ $t('about.ourValues') }}</p>
      <h2 class="val-title">{{ $t('about.ourValuesDesc') }}</h2>
      <div class="val-title-divider" aria-hidden="true"></div>
    </div>

    <!-- Cards grid -->
    <v-row>
      <v-col
        v-for="(value, index) in values"
        :key="index"
        cols="12"
        sm="6"
        md="3"
      >
        <div
          class="val-card"
          :class="{ 'is-visible': inView }"
          :style="{ transitionDelay: `${index * 0.12}s` }"
        >
          <!-- Top accent line -->
          <div class="val-card-bar" :style="{ background: value.gradient }" aria-hidden="true"></div>

          <div class="val-card-inner">
            <!-- Icon badge -->
            <div class="val-icon" :style="{ background: value.gradient, boxShadow: `0 6px 20px ${value.shadow}` }" aria-hidden="true">
              <v-icon size="26" color="white">{{ value.icon }}</v-icon>
            </div>

            <!-- Title -->
            <h3 class="val-card-title">{{ $t(value.title) }}</h3>

            <!-- Desc -->
            <p class="val-card-desc">{{ $t(value.description) }}</p>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
// ── Brand palette ─────────────────────────────────────────────────────
$blue:   #3949ab;
$purple: #7c3aed;
$red:    #e53935;

// ── Wrap ──────────────────────────────────────────────────────────────
.val-wrap {
  padding: 40px 0 60px;
}

// ── Header ────────────────────────────────────────────────────────────
.val-header {
  text-align: center;
  margin-bottom: 56px;
}

.val-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: $blue;
  margin-bottom: 12px;

  .theme--dark & {
    color: lighten($blue, 28%);
  }

  [dir='rtl'] & {
    letter-spacing: 0;
    text-transform: none;
  }
}

.val-title {
  font-size: clamp(1.3rem, 3vw, 1.8rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.4;
  color: #1a1a2e;
  max-width: 560px;
  margin: 0 auto 20px;

  .theme--dark & {
    color: #eeeef5;
  }

  [dir='rtl'] & {
    letter-spacing: 0;
    line-height: 1.65;
  }
}

.val-title-divider {
  width: 48px;
  height: 3px;
  border-radius: 2px;
  margin: 0 auto;
  background: linear-gradient(90deg, $blue, $purple, $red);
}

// ── Card ──────────────────────────────────────────────────────────────
.val-card {
  height: 100%;
  border-radius: 20px;
  border: 1px solid rgba($blue, 0.1);
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 4px 20px rgba($blue, 0.04);
  overflow: hidden;
  position: relative;
  opacity: 0;
  transform: translateY(28px);
  transition:
    opacity 0.65s ease-out,
    transform 0.65s ease-out,
    box-shadow 0.28s ease,
    border-color 0.28s ease;

  .theme--dark & {
    background: rgba(18, 18, 32, 0.88);
    border-color: rgba(255, 255, 255, 0.07);
  }

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    box-shadow: 0 16px 44px rgba($blue, 0.13);
    border-color: rgba($blue, 0.18);
    transform: translateY(-6px);

    .theme--dark & {
      box-shadow: 0 16px 44px rgba(0, 0, 0, 0.32);
    }

    .val-icon {
      transform: scale(1.1) rotate(-5deg);
    }
  }
}

// ── Top accent bar ────────────────────────────────────────────────────
.val-card-bar {
  height: 3px;
  width: 100%;
}

// ── Inner padding ─────────────────────────────────────────────────────
.val-card-inner {
  padding: 28px 24px 32px;
}

// ── Icon badge ────────────────────────────────────────────────────────
.val-icon {
  width: 48px;
  height: 48px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

// ── Card title ────────────────────────────────────────────────────────
.val-card-title {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #1a1a2e;
  margin-bottom: 10px;
  line-height: 1.35;

  .theme--dark & {
    color: #eeeef5;
  }

  [dir='rtl'] & {
    letter-spacing: 0;
    line-height: 1.6;
  }
}

// ── Card description ──────────────────────────────────────────────────
.val-card-desc {
  font-size: 0.88rem;
  font-weight: 400;
  line-height: 1.7;
  color: #6b6b88;
  margin: 0;

  .theme--dark & {
    color: #9090aa;
  }

  [dir='rtl'] & {
    line-height: 1.9;
  }
}

// ── Responsive ────────────────────────────────────────────────────────
@media (max-width: 600px) {
  .val-wrap {
    padding: 24px 0 40px;
  }

  .val-header {
    margin-bottom: 36px;
  }

  .val-card:hover {
    transform: translateY(-3px);
  }
}
</style>

<script>
export default {
  data () {
    return {
      inView: false,
      values: [
        {
          icon: 'mdi-shield-check',
          title: 'about.trustValue',
          description: 'about.trustValueDesc',
          gradient: 'linear-gradient(135deg, #3949ab, #5c6bc0)',
          shadow: 'rgba(57, 73, 171, 0.32)'
        },
        {
          icon: 'mdi-lightbulb-on',
          title: 'about.innovationValue',
          description: 'about.innovationValueDesc',
          gradient: 'linear-gradient(135deg, #7c3aed, #9d5bf7)',
          shadow: 'rgba(124, 58, 237, 0.3)'
        },
        {
          icon: 'mdi-account-group',
          title: 'about.teamworkValue',
          description: 'about.teamworkValueDesc',
          gradient: 'linear-gradient(135deg, #7c3aed, #e53935)',
          shadow: 'rgba(124, 58, 237, 0.25)'
        },
        {
          icon: 'mdi-star-circle',
          title: 'about.excellenceValue',
          description: 'about.excellenceValueDesc',
          gradient: 'linear-gradient(135deg, #e53935, #f06292)',
          shadow: 'rgba(229, 57, 53, 0.28)'
        }
      ]
    }
  },
  mounted () {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.inView = true
          observer.disconnect()
        }
      },
      { threshold: 0.15 }
    )
    observer.observe(this.$refs.wrap)
  }
}
</script>
